import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  useNavigate,
} from "react-router-dom";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";

/* =========================================================
   CONSTANTS
========================================================= */

const COUNSELLING_TYPES = [
  "Counselling",
  "Phone",
  "Online",
  "Walk-in",
  "Campus Visit",
];

const STATUSES = [
  "Pending",
  "Completed",
  "Cancelled",
];

const PRIORITIES = [
  "Low",
  "Medium",
  "High",
];

/* =========================================================
   HELPERS
========================================================= */

const getLeadName = (lead) => {
  if (!lead) {
    return "Unnamed Lead";
  }

  if (lead.name) {
    return lead.name;
  }

  return [
    lead.firstName,
    lead.lastName,
  ]
    .filter(Boolean)
    .join(" ") || "Unnamed Lead";
};

const getLeadPhone = (lead) => {
  return (
    lead?.phone ||
    lead?.mobile ||
    lead?.whatsapp ||
    "-"
  );
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offset =
    date.getTimezoneOffset();

  const localDate = new Date(
    date.getTime() -
      offset * 60 * 1000
  );

  return localDate
    .toISOString()
    .slice(0, 16);
};

const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
      return "success";

    case "Cancelled":
      return "secondary";

    case "Pending":
      return "warning";

    case "Overdue":
      return "danger";

    default:
      return "light";
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case "High":
      return "danger";

    case "Medium":
      return "warning";

    case "Low":
      return "success";

    default:
      return "light";
  }
};

const isToday = (value) => {
  if (!value) {
    return false;
  }

  const date = new Date(value);
  const today = new Date();

  return (
    date.getFullYear() ===
      today.getFullYear() &&
    date.getMonth() ===
      today.getMonth() &&
    date.getDate() ===
      today.getDate()
  );
};

const isOverdue = (item) => {
  if (
    !item?.scheduledAt ||
    item?.status !== "Pending"
  ) {
    return false;
  }

  return (
    new Date(
      item.scheduledAt
    ).getTime() < Date.now()
  );
};

/* =========================================================
   DEFAULT FORM
========================================================= */

const emptyForm = {
  leadId: "",
  scheduledAt: "",
  type: "Counselling",
  priority: "Medium",
  status: "Pending",
  note: "",
};

/* =========================================================
   COMPONENT
========================================================= */

const Counselling = () => {
  const navigate = useNavigate();

  const [followUps, setFollowUps] =
    useState([]);

  const [leads, setLeads] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("all");

  const [dateFilter, setDateFilter] =
    useState("all");

  const [priorityFilter, setPriorityFilter] =
    useState("all");

  const [modalOpen, setModalOpen] =
    useState(false);

  const [editingItem, setEditingItem] =
    useState(null);

  const [form, setForm] =
    useState(emptyForm);

  /* =======================================================
     LOAD DATA
  ======================================================= */

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        followUpResponse,
        leadResponse,
      ] = await Promise.all([
        api.get("/follow-ups"),
        api.get("/leads", {
          params: {
            limit: 100,
          },
        }),
      ]);

      const followUpData =
        followUpResponse?.data?.data;

      const leadData =
        leadResponse?.data?.data;

      let followUpList = [];

      if (
        Array.isArray(followUpData)
      ) {
        followUpList =
          followUpData;
      } else if (
        Array.isArray(
          followUpData?.followUps
        )
      ) {
        followUpList =
          followUpData.followUps;
      }

      let leadList = [];

      if (
        Array.isArray(leadData)
      ) {
        leadList = leadData;
      } else if (
        Array.isArray(
          leadData?.leads
        )
      ) {
        leadList =
          leadData.leads;
      }

      /*
       * Counselling is represented
       * through FollowUp records.
       *
       * Keep records whose type is
       * counselling-related.
       */
      const counsellingList =
        followUpList.filter(
          (item) => {
            const type =
              String(
                item?.type || ""
              ).toLowerCase();

            return (
              type === "counselling" ||
              type === "counseling" ||
              type === "campus visit" ||
              type === "online"
            );
          }
        );

      setFollowUps(
        counsellingList
      );

      setLeads(leadList);
    } catch (requestError) {
      console.error(
        "Failed to load counselling data:",
        requestError
      );

      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Failed to load counselling data."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* =======================================================
     LEAD LOOKUP
  ======================================================= */

  const getFollowUpLead = (item) => {
    if (
      item?.leadId &&
      typeof item.leadId === "object"
    ) {
      return item.leadId;
    }

    const leadId =
      item?.leadId?._id ||
      item?.leadId;

    return leads.find(
      (lead) =>
        String(
          lead?._id
        ) === String(leadId)
    );
  };

  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary = useMemo(() => {
    const today =
      followUps.filter(
        (item) =>
          isToday(
            item?.scheduledAt
          ) &&
          item?.status === "Pending"
      ).length;

    const upcoming =
      followUps.filter(
        (item) =>
          item?.scheduledAt &&
          new Date(
            item.scheduledAt
          ).getTime() > Date.now() &&
          item?.status === "Pending"
      ).length;

    const completed =
      followUps.filter(
        (item) =>
          item?.status === "Completed"
      ).length;

    const overdue =
      followUps.filter(
        isOverdue
      ).length;

    const cancelled =
      followUps.filter(
        (item) =>
          item?.status === "Cancelled"
      ).length;

    return {
      total: followUps.length,
      today,
      upcoming,
      completed,
      overdue,
      cancelled,
    };
  }, [followUps]);

  /* =======================================================
     FILTER
  ======================================================= */

  const filteredItems = useMemo(() => {
    const searchValue =
      search
        .trim()
        .toLowerCase();

    return followUps.filter(
      (item) => {
        const lead =
          getFollowUpLead(item);

        const leadName =
          getLeadName(
            lead
          ).toLowerCase();

        const phone =
          getLeadPhone(
            lead
          ).toLowerCase();

        const note =
          String(
            item?.note || ""
          ).toLowerCase();

        const matchesSearch =
          !searchValue ||
          leadName.includes(
            searchValue
          ) ||
          phone.includes(
            searchValue
          ) ||
          note.includes(
            searchValue
          );

        const matchesStatus =
          statusFilter ===
            "all" ||
          item?.status ===
            statusFilter;

        const matchesPriority =
          priorityFilter ===
            "all" ||
          item?.priority ===
            priorityFilter;

        let matchesDate = true;

        if (
          dateFilter ===
          "today"
        ) {
          matchesDate =
            isToday(
              item?.scheduledAt
            );
        }

        if (
          dateFilter ===
          "overdue"
        ) {
          matchesDate =
            isOverdue(item);
        }

        if (
          dateFilter ===
          "upcoming"
        ) {
          matchesDate =
            item?.status ===
              "Pending" &&
            item?.scheduledAt &&
            new Date(
              item.scheduledAt
            ).getTime() >
              Date.now() &&
            !isToday(
              item.scheduledAt
            );
        }

        if (
          dateFilter ===
          "completed"
        ) {
          matchesDate =
            item?.status ===
            "Completed";
        }

        return (
          matchesSearch &&
          matchesStatus &&
          matchesPriority &&
          matchesDate
        );
      }
    );
  }, [
    followUps,
    leads,
    search,
    statusFilter,
    priorityFilter,
    dateFilter,
  ]);

  /* =======================================================
     FORM
  ======================================================= */

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );
  };

  const openCreateModal = () => {
    setEditingItem(null);

    setForm({
      ...emptyForm,
      scheduledAt:
        toInputDateTime(
          new Date(
            Date.now() +
              60 * 60 * 1000
          )
        ),
    });

    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const openEditModal = (
    item
  ) => {
    const leadId =
      item?.leadId?._id ||
      item?.leadId ||
      "";

    setEditingItem(item);

    setForm({
      leadId,
      scheduledAt:
        toInputDateTime(
          item?.scheduledAt
        ),
      type:
        item?.type ||
        "Counselling",
      priority:
        item?.priority ||
        "Medium",
      status:
        item?.status ||
        "Pending",
      note:
        item?.note || "",
    });

    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  };

  /* =======================================================
     CREATE / UPDATE
  ======================================================= */

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!form.leadId) {
      setError(
        "Please select a lead."
      );
      return;
    }

    if (!form.scheduledAt) {
      setError(
        "Counselling date and time are required."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        leadId:
          form.leadId,
        scheduledAt:
          form.scheduledAt,
        type:
          form.type,
        priority:
          form.priority,
        status:
          form.status,
        note:
          form.note,
      };

      if (editingItem?._id) {
        await api.put(
          `/follow-ups/${editingItem._id}`,
          payload
        );

        setSuccess(
          "Counselling session updated successfully."
        );
      } else {
        await api.post(
          "/follow-ups",
          payload
        );

        setSuccess(
          "Counselling session scheduled successfully."
        );
      }

      setModalOpen(false);
      setEditingItem(null);
      setForm(emptyForm);

      await loadData();
    } catch (requestError) {
      console.error(
        "Failed to save counselling:",
        requestError
      );

      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Failed to save counselling session."
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     COMPLETE
  ======================================================= */

  const handleComplete = async (
    item
  ) => {
    try {
      setError("");
      setSuccess("");

      await api.put(
        `/follow-ups/${item._id}/complete`,
        {
          note:
            item?.note || "",
        }
      );

      setSuccess(
        "Counselling session marked as completed."
      );

      await loadData();
    } catch (requestError) {
      console.error(
        "Failed to complete counselling:",
        requestError
      );

      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Failed to complete counselling session."
      );
    }
  };

  /* =======================================================
     CANCEL
  ======================================================= */

  const handleCancel = async (
    item
  ) => {
    const confirmed =
      window.confirm(
        "Are you sure you want to cancel this counselling session?"
      );

    if (!confirmed) {
      return;
    }

    try {
      setError("");
      setSuccess("");

      await api.put(
        `/follow-ups/${item._id}/cancel`,
        {
          note:
            item?.note || "",
        }
      );

      setSuccess(
        "Counselling session cancelled."
      );

      await loadData();
    } catch (requestError) {
      console.error(
        "Failed to cancel counselling:",
        requestError
      );

      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Failed to cancel counselling session."
      );
    }
  };

  /* =======================================================
     VIEW LEAD
  ======================================================= */

  const openLead = (
    item
  ) => {
    const lead =
      getFollowUpLead(item);

    const leadId =
      lead?._id ||
      item?.leadId?._id ||
      item?.leadId;

    if (!leadId) {
      return;
    }

    navigate(
      `/leads/${leadId}`
    );
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Counselling"
            pageTitle="Leads"
          />

          <div className="text-center py-5">
            <Spinner color="primary" />

            <div className="text-muted mt-2">
              Loading counselling sessions...
            </div>
          </div>
        </Container>
      </div>
    );
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Counselling"
            pageTitle="Leads"
          />

          {/* =================================================
              ALERTS
          ================================================= */}

          {error && (
            <Alert
              color="danger"
              toggle={() =>
                setError("")
              }
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              color="success"
              toggle={() =>
                setSuccess("")
              }
            >
              {success}
            </Alert>
          )}

          {/* =================================================
              HEADER
          ================================================= */}

          <Row className="align-items-center mb-3">
            <Col>
              <div>
                <h4 className="mb-1">
                  Counselling
                </h4>

                <p className="text-muted mb-0">
                  Manage counselling sessions,
                  appointments and student interactions.
                </p>
              </div>
            </Col>

            <Col
              xs="auto"
            >
              <Button
                color="primary"
                onClick={
                  openCreateModal
                }
              >
                <i className="ri-add-line me-1" />
                Schedule Counselling
              </Button>
            </Col>
          </Row>

          {/* =================================================
              SUMMARY CARDS
          ================================================= */}

          <Row>
            <Col
              xl={2}
              md={4}
              sm={6}
            >
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">
                        Total
                      </p>

                      <h4 className="mb-0">
                        {summary.total}
                      </h4>
                    </div>

                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                        <i className="ri-calendar-line" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col
              xl={2}
              md={4}
              sm={6}
            >
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">
                        Today
                      </p>

                      <h4 className="mb-0">
                        {summary.today}
                      </h4>
                    </div>

                    <div className="avatar-sm">
                      <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                        <i className="ri-calendar-check-line" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col
              xl={2}
              md={4}
              sm={6}
            >
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">
                        Upcoming
                      </p>

                      <h4 className="mb-0">
                        {summary.upcoming}
                      </h4>
                    </div>

                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                        <i className="ri-time-line" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col
              xl={2}
              md={4}
              sm={6}
            >
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">
                        Overdue
                      </p>

                      <h4 className="mb-0 text-danger">
                        {summary.overdue}
                      </h4>
                    </div>

                    <div className="avatar-sm">
                      <span className="avatar-title bg-danger-subtle text-danger rounded fs-3">
                        <i className="ri-alarm-warning-line" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col
              xl={2}
              md={4}
              sm={6}
            >
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">
                        Completed
                      </p>

                      <h4 className="mb-0 text-success">
                        {summary.completed}
                      </h4>
                    </div>

                    <div className="avatar-sm">
                      <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                        <i className="ri-checkbox-circle-line" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col
              xl={2}
              md={4}
              sm={6}
            >
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted mb-1">
                        Cancelled
                      </p>

                      <h4 className="mb-0">
                        {summary.cancelled}
                      </h4>
                    </div>

                    <div className="avatar-sm">
                      <span className="avatar-title bg-secondary-subtle text-secondary rounded fs-3">
                        <i className="ri-close-circle-line" />
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* =================================================
              FILTERS
          ================================================= */}

          <Card>
            <CardBody>
              <Row className="g-3">
                <Col
                  xl={4}
                  lg={4}
                >
                  <Label className="form-label">
                    Search
                  </Label>

                  <div className="search-box">
                    <Input
                      type="text"
                      value={search}
                      onChange={(event) =>
                        setSearch(
                          event.target.value
                        )
                      }
                      placeholder="Search student, phone or notes..."
                    />

                    <i className="ri-search-line search-icon" />
                  </div>
                </Col>

                <Col
                  xl={2}
                  lg={2}
                  md={4}
                >
                  <Label className="form-label">
                    Date
                  </Label>

                  <Input
                    type="select"
                    value={dateFilter}
                    onChange={(event) =>
                      setDateFilter(
                        event.target.value
                      )
                    }
                  >
                    <option value="all">
                      All
                    </option>

                    <option value="today">
                      Today
                    </option>

                    <option value="overdue">
                      Overdue
                    </option>

                    <option value="upcoming">
                      Upcoming
                    </option>

                    <option value="completed">
                      Completed
                    </option>
                  </Input>
                </Col>

                <Col
                  xl={2}
                  lg={2}
                  md={4}
                >
                  <Label className="form-label">
                    Status
                  </Label>

                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(event) =>
                      setStatusFilter(
                        event.target.value
                      )
                    }
                  >
                    <option value="all">
                      All
                    </option>

                    {STATUSES.map(
                      (status) => (
                        <option
                          key={status}
                          value={status}
                        >
                          {status}
                        </option>
                      )
                    )}
                  </Input>
                </Col>

                <Col
                  xl={2}
                  lg={2}
                  md={4}
                >
                  <Label className="form-label">
                    Priority
                  </Label>

                  <Input
                    type="select"
                    value={priorityFilter}
                    onChange={(event) =>
                      setPriorityFilter(
                        event.target.value
                      )
                    }
                  >
                    <option value="all">
                      All
                    </option>

                    {PRIORITIES.map(
                      (priority) => (
                        <option
                          key={priority}
                          value={priority}
                        >
                          {priority}
                        </option>
                      )
                    )}
                  </Input>
                </Col>

                <Col
                  xl={2}
                  lg={2}
                  md={4}
                  className="d-flex align-items-end"
                >
                  <Button
                    color="light"
                    className="w-100"
                    onClick={() => {
                      setSearch("");
                      setDateFilter(
                        "all"
                      );
                      setStatusFilter(
                        "all"
                      );
                      setPriorityFilter(
                        "all"
                      );
                    }}
                  >
                    <i className="ri-refresh-line me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* =================================================
              TABLE
          ================================================= */}

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="card-title mb-1">
                    Counselling Sessions
                  </h5>

                  <p className="text-muted mb-0">
                    {filteredItems.length} session
                    {filteredItems.length !== 1
                      ? "s"
                      : ""}{" "}
                    found
                  </p>
                </div>
              </div>

              <div className="table-responsive">
                <Table
                  hover
                  responsive
                  className="align-middle mb-0"
                >
                  <thead className="table-light">
                    <tr>
                      <th>
                        Student / Lead
                      </th>

                      <th>
                        Contact
                      </th>

                      <th>
                        Scheduled
                      </th>

                      <th>
                        Type
                      </th>

                      <th>
                        Priority
                      </th>

                      <th>
                        Status
                      </th>

                      <th>
                        Notes
                      </th>

                      <th className="text-end">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredItems.length ===
                    0 ? (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center text-muted py-5"
                        >
                          <div className="mb-2">
                            <i className="ri-calendar-close-line fs-1" />
                          </div>

                          <div className="fw-semibold">
                            No counselling sessions found.
                          </div>

                          <div className="small mt-1">
                            Schedule a counselling session
                            to get started.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map(
                        (item) => {
                          const lead =
                            getFollowUpLead(
                              item
                            );

                          const overdue =
                            isOverdue(
                              item
                            );

                          return (
                            <tr
                              key={
                                item._id
                              }
                            >
                              <td>
                                <div
                                  className="fw-semibold"
                                  style={{
                                    cursor:
                                      lead?._id
                                        ? "pointer"
                                        : "default",
                                  }}
                                  onClick={() =>
                                    openLead(
                                      item
                                    )
                                  }
                                >
                                  {getLeadName(
                                    lead
                                  )}
                                </div>

                                {lead?.leadNumber && (
                                  <div className="text-muted small">
                                    {
                                      lead.leadNumber
                                    }
                                  </div>
                                )}
                              </td>

                              <td>
                                <div>
                                  {getLeadPhone(
                                    lead
                                  )}
                                </div>

                                {lead?.email && (
                                  <div className="text-muted small">
                                    {
                                      lead.email
                                    }
                                  </div>
                                )}
                              </td>

                              <td
                                className={
                                  overdue
                                    ? "text-danger fw-semibold"
                                    : ""
                                }
                              >
                                {formatDate(
                                  item?.scheduledAt
                                )}

                                {overdue && (
                                  <div className="small text-danger">
                                    Overdue
                                  </div>
                                )}
                              </td>

                              <td>
                                <Badge
                                  color="light"
                                  className="text-dark"
                                >
                                  {item?.type ||
                                    "Counselling"}
                                </Badge>
                              </td>

                              <td>
                                <Badge
                                  color={getPriorityColor(
                                    item?.priority
                                  )}
                                >
                                  {item?.priority ||
                                    "Medium"}
                                </Badge>
                              </td>

                              <td>
                                <Badge
                                  color={getStatusColor(
                                    item?.status
                                  )}
                                >
                                  {item?.status ||
                                    "Pending"}
                                </Badge>
                              </td>

                              <td>
                                <span
                                  className="text-muted"
                                  title={
                                    item?.note ||
                                    ""
                                  }
                                >
                                  {item?.note
                                    ? item.note
                                        .length >
                                      45
                                      ? `${item.note.slice(
                                          0,
                                          45
                                        )}...`
                                      : item.note
                                    : "-"}
                                </span>
                              </td>

                              <td className="text-end">
                                <div className="d-flex justify-content-end gap-1">
                                  <Button
                                    color="light"
                                    size="sm"
                                    title="View lead"
                                    onClick={() =>
                                      openLead(
                                        item
                                      )
                                    }
                                  >
                                    <i className="ri-eye-line" />
                                  </Button>

                                  {item?.status ===
                                    "Pending" && (
                                    <>
                                      <Button
                                        color="light"
                                        size="sm"
                                        title="Edit"
                                        onClick={() =>
                                          openEditModal(
                                            item
                                          )
                                        }
                                      >
                                        <i className="ri-pencil-line" />
                                      </Button>

                                      <Button
                                        color="success"
                                        size="sm"
                                        title="Complete"
                                        onClick={() =>
                                          handleComplete(
                                            item
                                          )
                                        }
                                      >
                                        <i className="ri-check-line" />
                                      </Button>

                                      <Button
                                        color="danger"
                                        outline
                                        size="sm"
                                        title="Cancel"
                                        onClick={() =>
                                          handleCancel(
                                            item
                                          )
                                        }
                                      >
                                        <i className="ri-close-line" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* =====================================================
          CREATE / EDIT MODAL
      ====================================================== */}

      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
        size="lg"
      >
        <Form
          onSubmit={
            handleSubmit
          }
        >
          <ModalHeader
            toggle={closeModal}
          >
            {editingItem
              ? "Edit Counselling Session"
              : "Schedule Counselling"}
          </ModalHeader>

          <ModalBody>
            {error && (
              <Alert color="danger">
                {error}
              </Alert>
            )}

            <Row className="g-3">
              <Col md={6}>
                <Label className="form-label">
                  Student / Lead{" "}
                  <span className="text-danger">
                    *
                  </span>
                </Label>

                <Input
                  type="select"
                  name="leadId"
                  value={
                    form.leadId
                  }
                  onChange={
                    handleChange
                  }
                  required
                >
                  <option value="">
                    Select Lead
                  </option>

                  {leads.map(
                    (lead) => (
                      <option
                        key={
                          lead._id
                        }
                        value={
                          lead._id
                        }
                      >
                        {getLeadName(
                          lead
                        )}
                        {lead.leadNumber
                          ? ` — ${lead.leadNumber}`
                          : ""}
                      </option>
                    )
                  )}
                </Input>

                {leads.length ===
                  0 && (
                  <div className="text-danger small mt-1">
                    No leads available.
                  </div>
                )}
              </Col>

              <Col md={6}>
                <Label className="form-label">
                  Counselling Date & Time{" "}
                  <span className="text-danger">
                    *
                  </span>
                </Label>

                <Input
                  type="datetime-local"
                  name="scheduledAt"
                  value={
                    form.scheduledAt
                  }
                  onChange={
                    handleChange
                  }
                  required
                />
              </Col>

              <Col md={4}>
                <Label className="form-label">
                  Type
                </Label>

                <Input
                  type="select"
                  name="type"
                  value={
                    form.type
                  }
                  onChange={
                    handleChange
                  }
                >
                  {COUNSELLING_TYPES.map(
                    (type) => (
                      <option
                        key={type}
                        value={type}
                      >
                        {type}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col md={4}>
                <Label className="form-label">
                  Priority
                </Label>

                <Input
                  type="select"
                  name="priority"
                  value={
                    form.priority
                  }
                  onChange={
                    handleChange
                  }
                >
                  {PRIORITIES.map(
                    (priority) => (
                      <option
                        key={priority}
                        value={priority}
                      >
                        {priority}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col md={4}>
                <Label className="form-label">
                  Status
                </Label>

                <Input
                  type="select"
                  name="status"
                  value={
                    form.status
                  }
                  onChange={
                    handleChange
                  }
                >
                  {STATUSES.map(
                    (status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {status}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col xs={12}>
                <Label className="form-label">
                  Counselling Notes
                </Label>

                <Input
                  type="textarea"
                  name="note"
                  rows="5"
                  value={
                    form.note
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter counselling discussion, requirements, parent/student concerns, next steps..."
                />
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              color="light"
              type="button"
              onClick={
                closeModal
              }
              disabled={
                saving
              }
            >
              Cancel
            </Button>

            <Button
              color="primary"
              type="submit"
              disabled={
                saving
              }
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-1"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1" />
                  {editingItem
                    ? "Update Session"
                    : "Schedule Session"}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default Counselling;